// Pure logic for the KR update experience (no Firebase / Vue imports so it is
// unit-testable standalone). Shared semantics live in the cross-repo contract:
// docs/superpowers/plans/2026-07-13-kr-update-experience.md (C1–C3, C5, C6).

export type KrStatus = 'on_track' | 'at_risk' | 'off_track' | 'completed';

export interface KrHistoryEntry {
  // KR-level number at the time of the entry: the team rollup (sum of
  // owner_progress) for individual-mode KRs, current_value otherwise. This is
  // the cross-repo convention the mobile app, kr-automation, and the
  // notification cron all share.
  value: number;
  // Individual mode only: the owner's own slice value after this update.
  // Absent on collaborative KRs and on legacy entries (older web/CC writes
  // stored the slice in `value` instead — display code must not assume it
  // can reconstruct slices for entries missing this field).
  owner_value?: number;
  // Individual mode only: whose slice owner_value belongs to. Usually equals
  // updated_by, but differs when a manager updates on an owner's behalf.
  owner_email?: string;
  updated_at: string;
  updated_by: string;
  note?: string;
  status?: KrStatus;
}

export interface KrLike {
  status?: KrStatus;
  // 'manual' ⇒ a human deliberately set the status; absent ⇒ derived from progress.
  status_source?: 'manual';
  start_value?: number;
  target_value?: number;
  current_value?: number;
  mode?: string;
  owner_progress?: Record<string, number>;
  progress_history?: KrHistoryEntry[];
}

// The reserved actor string automation writers stamp into progress_history.
// Must NEVER be rendered as a person's name (contract C5).
export const AUTOMATION_ACTOR = 'system:automation';

// Human-readable names for automation toolkits (contract C5 provenance line).
export const TOOLKIT_DISPLAY_NAMES: Record<string, string> = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  stripe: 'Stripe',
  linear: 'Linear',
  notion: 'Notion',
  github: 'GitHub',
  slack: 'Slack',
  zendesk: 'Zendesk',
  intercom: 'Intercom',
  mixpanel: 'Mixpanel',
  google_analytics: 'Google Analytics',
  google_sheets: 'Google Sheets',
  jira: 'Jira',
};

export function toolkitDisplayName(slug?: string): string {
  const key = (slug || '').toLowerCase();
  if (TOOLKIT_DISPLAY_NAMES[key]) return TOOLKIT_DISPLAY_NAMES[key];
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : 'source';
}

export function isAutomationActor(updatedBy?: string): boolean {
  return updatedBy === AUTOMATION_ACTOR;
}

// Shared thresholds — identical in HR console, command center, mobile, and the
// kr-automation runner. Do not change one without changing all (contract C3).
export function deriveKrStatus(progress: number): KrStatus {
  if (progress >= 100) return 'completed';
  if (progress >= 60) return 'on_track';
  if (progress >= 30) return 'at_risk';
  return 'off_track';
}

// Effective value: sum of per-owner progress in individual mode, else current_value.
export function getEffectiveKrValue(kr: KrLike): number {
  if (kr.mode === 'individual' && kr.owner_progress && Object.keys(kr.owner_progress).length > 0) {
    return Object.values(kr.owner_progress).reduce((sum, v) => sum + (Number(v) || 0), 0);
  }
  return Number(kr.current_value) || 0;
}

/**
 * Build a progress_history entry for an ALREADY-UPDATED kr (owner_progress /
 * current_value applied first). Central spot for the entry-value convention:
 * `value` is always the KR-level rollup; the actor's own slice rides in
 * `owner_value` on individual-mode entries so timelines can lead with the
 * number the person actually updated.
 */
export function buildProgressHistoryEntry(
  kr: KrLike,
  args: { updatedBy: string; note?: string; status?: KrStatus },
): KrHistoryEntry {
  const entry: KrHistoryEntry = {
    value: getEffectiveKrValue(kr),
    updated_at: new Date().toISOString(),
    updated_by: args.updatedBy,
    note: args.note || '',
  };
  if (args.status) entry.status = args.status;
  if (
    kr.mode === 'individual'
    && args.updatedBy
    && kr.owner_progress
    && typeof kr.owner_progress[args.updatedBy] === 'number'
  ) {
    entry.owner_value = kr.owner_progress[args.updatedBy];
    entry.owner_email = args.updatedBy;
  }
  return entry;
}

export function calculateKrProgress(kr: KrLike): number {
  const start = Number(kr.start_value) || 0;
  const target = Number(kr.target_value) || 0;
  if (target === start) return 0;
  const raw = ((getEffectiveKrValue(kr) - start) / (target - start)) * 100;
  return Math.min(Math.max(raw, 0), 100);
}

/**
 * Contract C3 — manual status stickiness. Apply after computing newProgress
 * for a KR. A human-set status (status_source === 'manual') survives value
 * updates; only derived completion overrides it (and clears the override).
 * Mutates and returns `kr`.
 */
export function applyDerivedStatus<T extends KrLike>(kr: T, newProgress: number): T {
  const derived = deriveKrStatus(newProgress);
  if (kr.status_source === 'manual' && derived !== 'completed') {
    // keep kr.status untouched — a human set it deliberately
    return kr;
  }
  kr.status = derived;
  if (derived === 'completed') {
    // completion clears the override
    delete kr.status_source;
  }
  return kr;
}

/**
 * Contract C3 — the "Auto" dropdown option. Removes the manual override and
 * re-derives status from current progress. Mutates `kr`; reports whether the
 * visible status actually changed (callers append a history entry only then).
 */
export function revertKrStatusToAuto<T extends KrLike>(kr: T): { changed: boolean; status: KrStatus } {
  const previous = kr.status;
  const derived = deriveKrStatus(calculateKrProgress(kr));
  delete kr.status_source;
  kr.status = derived;
  return { changed: previous !== derived, status: derived };
}

/**
 * Contract C3 — a manual status set from the dropdown. Mutates `kr`.
 * System writers (no human actor) write the status without claiming a manual
 * override — and drop any stale one, so the "set manually" affordance never
 * points at a status a human didn't choose.
 */
export function applyStatusWrite<T extends KrLike>(kr: T, status: KrStatus, isHumanActor: boolean): T {
  kr.status = status;
  if (isHumanActor) {
    kr.status_source = 'manual';
  } else {
    delete kr.status_source;
  }
  return kr;
}

/**
 * Contract C1 — locate a goal inside a doc's goals[] array. Matches on
 * goal_id_ai, with the legacy `${docId}_${index}` fallback for goals that
 * predate stable ids.
 */
export function findGoalIndexInGoals(goals: any[] | undefined, goalId: string, docId: string): number {
  if (!Array.isArray(goals) || !goalId) return -1;
  let idx = goals.findIndex((g: any) => g && g.goal_id_ai === goalId);
  if (idx === -1) {
    const fallbackMatch = goalId.match(/^(.+)_(\d+)$/);
    if (fallbackMatch && fallbackMatch[1] === docId) {
      const i = parseInt(fallbackMatch[2], 10);
      if (i >= 0 && i < goals.length) idx = i;
    }
  }
  return idx;
}

/**
 * Contract C1 — choose which goals_team_ai doc to operate on when the field
 * query (company_id + team_id) returns more than one (legacy `--` docs can
 * coexist with canonical `-` docs). Preference order:
 *   1. the doc that actually contains `goalId` (when provided),
 *   2. the first doc with a non-empty goals array,
 *   3. the first doc.
 * Returns -1 for an empty list.
 */
export function pickGoalsTeamDocIndex(
  docs: Array<{ id: string; goals?: any[] }>,
  goalId?: string,
): number {
  if (!Array.isArray(docs) || docs.length === 0) return -1;
  if (goalId) {
    const withGoal = docs.findIndex((d) => findGoalIndexInGoals(d.goals, goalId, d.id) !== -1);
    if (withGoal !== -1) return withGoal;
  }
  const withGoals = docs.findIndex((d) => Array.isArray(d.goals) && d.goals.length > 0);
  if (withGoals !== -1) return withGoals;
  return 0;
}

export interface KrTimelineItem {
  // KR-level rollup at the time of the entry (see KrHistoryEntry.value).
  value: number;
  // The owner's own slice value, when the entry recorded one (individual
  // mode, post-owner_value writers). Null on collaborative/legacy entries.
  ownerValue: number | null;
  // Whose slice ownerValue is — owner_email fallback updated_by.
  ownerEmail: string;
  updated_at: string;
  updated_by: string;
  note: string;
  status?: KrStatus;
  // Rollup difference vs the chronologically previous entry; null for the first.
  delta: number | null;
  // Slice difference vs that owner's previous owner_value-carrying entry;
  // null when this or the previous slice point doesn't exist.
  ownerDelta: number | null;
  isAutomation: boolean;
}

/**
 * Contract C6 — render-ready update timeline from progress_history.
 * Chronologically sorts (defensive — writers append in order, but clock skew
 * happens), computes per-entry deltas against the previous entry, and returns
 * newest first. Rollup deltas diff consecutive entries regardless of actor
 * (coherent because `value` is the shared rollup).
 *
 * When `kr` (an individual-mode KR) is passed, every human entry gets an
 * ownerValue — the person's own slice, which the UI shows as the PRIMARY
 * number. Recorded owner_value is used verbatim; legacy entries are
 * reconstructed by walking backwards from the current owner_progress map
 * (each entry moves exactly one owner's slice, so its rollup delta IS that
 * owner's slice delta — the same anchoring the notification cron uses).
 * ownerDelta is that per-entry slice movement. Reconstruction stops for an
 * owner when the math turns impossible (negative slice — a sign of legacy
 * slice-in-value entries corrupting the chain) or when the actor isn't in
 * owner_progress (e.g. a non-owner edited); those rows fall back to
 * ownerValue null and the UI leads with the labeled team total instead.
 */
export function buildKrTimeline(
  history: KrHistoryEntry[] | undefined,
  kr?: KrLike,
): KrTimelineItem[] {
  const entries = Array.isArray(history) ? history.slice() : [];
  const decorated = entries.map((entry, index) => ({
    entry,
    index,
    time: Date.parse(entry && entry.updated_at ? entry.updated_at : '') || 0,
  }));
  decorated.sort((a, b) => (a.time - b.time) || (a.index - b.index));

  const items: KrTimelineItem[] = decorated.map(({ entry }, i) => {
    const prev = i > 0 ? decorated[i - 1].entry : null;
    const value = Number(entry.value) || 0;
    const hasSlice = typeof entry.owner_value === 'number' && Number.isFinite(entry.owner_value);
    return {
      value,
      ownerValue: hasSlice ? (entry.owner_value as number) : null,
      ownerEmail: entry.owner_email || entry.updated_by || '',
      updated_at: entry.updated_at || '',
      updated_by: entry.updated_by || '',
      note: entry.note || '',
      status: entry.status,
      delta: prev ? value - (Number(prev.value) || 0) : null,
      ownerDelta: null,
      isAutomation: isAutomationActor(entry.updated_by),
    };
  });

  if (kr && kr.mode === 'individual') {
    const startValue = Number(kr.start_value) || 0;
    // Slice AFTER each owner's next-newer entry — seeded from live state.
    const running: Record<string, number | undefined> = {};
    Object.entries(kr.owner_progress || {}).forEach(([email, v]) => {
      const n = Number(v);
      if (Number.isFinite(n)) running[email] = n;
    });
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      if (item.isAutomation || !item.ownerEmail) continue;
      const rollupDelta = item.delta !== null ? item.delta : item.value - startValue;
      const after = item.ownerValue !== null ? item.ownerValue : running[item.ownerEmail];
      if (typeof after !== 'number' || !Number.isFinite(after)) {
        running[item.ownerEmail] = undefined;
        continue;
      }
      const before = after - rollupDelta;
      if (before < Math.min(startValue, 0)) {
        // Impossible slice — legacy corruption. Keep a recorded value but
        // don't claim a delta, and stop reconstructing older entries.
        item.ownerDelta = null;
        running[item.ownerEmail] = undefined;
        continue;
      }
      item.ownerValue = after;
      item.ownerDelta = rollupDelta;
      running[item.ownerEmail] = before;
    }
  }

  return items.reverse();
}

/**
 * Newest history entry carrying an explicit status — the human judgment the
 * "set manually" affordance should attribute (contract C3 UI note).
 */
export function latestManualStatusEntry(history: KrHistoryEntry[] | undefined): KrHistoryEntry | null {
  const entries = Array.isArray(history) ? history : [];
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    if (entry && entry.status) return entry;
  }
  return null;
}

/**
 * Compact relative time used by the KR meta lines and the history timeline.
 * Accepts Firestore Timestamp, ISO string, epoch ms, or Date. `nowMs` is
 * injectable for tests.
 */
export function formatRelativeKrTime(value: any, nowMs: number = Date.now()): string {
  if (!value) return '';
  const then = typeof value.toDate === 'function' ? value.toDate()
    : value instanceof Date ? value
      : new Date(value);
  const ms = nowMs - then.getTime();
  if (Number.isNaN(ms)) return '';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return then.toLocaleDateString();
}

/** Newest history entry by timestamp (append order as tiebreak). */
export function latestHistoryEntry(history: KrHistoryEntry[] | undefined): KrHistoryEntry | null {
  const timeline = buildKrTimeline(history);
  if (timeline.length === 0) return null;
  const newest = timeline[0];
  return {
    value: newest.value,
    updated_at: newest.updated_at,
    updated_by: newest.updated_by,
    note: newest.note,
    status: newest.status,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Value + unit display formatting.
//
// The compact KR rows used to render `{{value}}{{unit}}` which produced
// "2leads / 10leads". These helpers centralize the spacing rules so every
// surface (row, history timeline, detail dialog) renders identically.

// Single-character currency marks that read best glued to the number as a
// prefix ("$500"). Multi-letter codes ("THB", "USD") fall through to the
// word-unit rule and render suffixed with a space ("500 THB").
export const KR_CURRENCY_UNITS = ['$', '€', '£', '฿', '¥', '₩', '₹'];

/**
 * Number formatting for KR values: at most 2 decimals, integers stay
 * integers. Mirrors `formatTargetNumber` in src/store/goals.ts — duplicated
 * here because goals.ts imports this module (importing back would cycle).
 */
export function formatKrNumber(n: number): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  if (Number.isInteger(num)) return String(num);
  return String(Math.round(num * 100) / 100);
}

/** True when the unit renders glued to the number ('%' suffix or currency prefix). */
export function isTightKrUnit(unit?: string): boolean {
  const u = (unit || '').trim();
  return u === '%' || KR_CURRENCY_UNITS.indexOf(u) !== -1;
}

/**
 * Render a KR value with its unit:
 *   '%'                  → tight suffix   "73%"
 *   currency symbol      → tight prefix   "$500"
 *   any word unit        → spaced suffix  "2 leads"
 *   empty / no unit      → bare number    "42"
 */
export function formatKrValue(value: number, unit?: string): string {
  const num = formatKrNumber(value);
  const u = (unit || '').trim();
  if (!u) return num;
  if (u === '%') return `${num}%`;
  if (KR_CURRENCY_UNITS.indexOf(u) !== -1) return `${u}${num}`;
  return `${num} ${u}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Per-owner progress rows (individual-mode KRs).
//
// Extracted from KeyResultRow's `individualProgress` / `getIndividualPercent`
// so the detail dialog reuses the exact same math instead of forking it.
// Display resolution (names, avatars) stays in the components — these are
// pure. `targetMap` comes from `computeOwnerTargetMap` in src/store/goals.ts.

export interface KrOwnerProgressRow {
  email: string;
  value: number;
  target: number;
  customized: boolean;
  percent: number;
}

/** Percent toward an owner's personal target, measured from the KR's start value. */
export function individualOwnerPercent(
  kr: { start_value?: number; target_value?: number },
  value: number,
  target?: number,
): number {
  const start = Number(kr.start_value) || 0;
  const fallbackEnd = Number(kr.target_value) || 0;
  const denomEnd = typeof target === 'number' && Number.isFinite(target) ? target : fallbackEnd;
  if (denomEnd === start) return 0;
  const raw = ((value - start) / (denomEnd - start)) * 100;
  return Math.min(Math.max(raw, 0), 100);
}

export function buildOwnerProgressRows(
  kr: {
    start_value?: number;
    target_value?: number;
    owner_emails?: string[];
    owner_progress?: Record<string, number>;
    owner_targets?: Record<string, number>;
  },
  targetMap: Record<string, number>,
): KrOwnerProgressRow[] {
  const emails = Array.isArray(kr.owner_emails) ? kr.owner_emails : [];
  const progress = kr.owner_progress || {};
  const overrides = kr.owner_targets || {};
  return emails.map((email) => {
    const value = Number(progress[email]) || 0;
    const target = Number(targetMap[email]) || 0;
    return {
      email,
      value,
      target,
      customized: Object.prototype.hasOwnProperty.call(overrides, email),
      percent: individualOwnerPercent(kr, value, target),
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Progress-over-time chart series (detail dialog).

export interface KrChartPoint {
  // Epoch ms — true time spacing on a linear x-axis (no time-adapter needed).
  t: number;
  value: number;
}

export interface KrChartSeries {
  // '' for the single rollup series; the slice owner's email for per-owner series.
  actor: string;
  points: KrChartPoint[];
}

export interface KrChartData {
  // True when the series are per-owner slice lines; false when a single
  // KR-level rollup line is returned (collaborative KRs, or the legacy-data
  // fallback below).
  grouped: boolean;
  series: KrChartSeries[];
}

/**
 * Chart-ready series from progress_history. Chronological, entries without a
 * parseable timestamp are dropped (they can't be placed on a time axis).
 *
 * With `groupByActor` (individual-mode KRs) each owner gets their own line,
 * plotted from `owner_value` — entry.value is the team rollup and must never
 * be drawn on a per-person line. Entries without owner_value (legacy) can't
 * be attributed to a slice, so when fewer than two slice points exist the
 * whole chart falls back to the single rollup line, which every entry can
 * contribute to honestly.
 */
export function buildKrChartSeries(
  history: KrHistoryEntry[] | undefined,
  options: { groupByActor?: boolean } = {},
): KrChartData {
  const entries = Array.isArray(history) ? history : [];
  const decorated = entries
    .map((entry, index) => ({
      entry,
      index,
      t: Date.parse(entry && entry.updated_at ? entry.updated_at : ''),
    }))
    .filter((d) => Number.isFinite(d.t));
  decorated.sort((a, b) => (a.t - b.t) || (a.index - b.index));
  if (decorated.length === 0) return { grouped: false, series: [] };

  const rollupSeries = (): KrChartData => ({
    grouped: false,
    series: [{
      actor: '',
      points: decorated.map((d) => ({ t: d.t, value: Number(d.entry.value) || 0 })),
    }],
  });

  if (!options.groupByActor) return rollupSeries();

  const order: string[] = [];
  const byOwner: Record<string, KrChartPoint[]> = {};
  let slicePoints = 0;
  for (const d of decorated) {
    const sliceValue = d.entry.owner_value;
    if (typeof sliceValue !== 'number' || !Number.isFinite(sliceValue)) continue;
    const owner = d.entry.owner_email || d.entry.updated_by || '';
    if (!byOwner[owner]) {
      byOwner[owner] = [];
      order.push(owner);
    }
    byOwner[owner].push({ t: d.t, value: sliceValue });
    slicePoints += 1;
  }
  if (slicePoints < 2) return rollupSeries();
  return {
    grouped: true,
    series: order.map((actor) => ({ actor, points: byOwner[actor] })),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Automation cadence display (metric_source.cadence).

export interface KrCadenceParts {
  kind: 'daily' | 'weekly' | 'monthly';
  time: string;
  // Capitalized weekday token for weekly cadences ('Monday').
  day: string;
  dayOfMonth: number;
}

/**
 * Structured parts of a metric_source cadence for display. Returns null when
 * there is no cadence. Mirrors the sentence logic of the automation dialog's
 * cadenceSummary; kept pure/structured here so the detail dialog can run the
 * parts through i18n templates.
 */
export function krCadenceParts(
  cadence?: { frequency?: string; day?: string; time?: string; day_of_month?: number },
): KrCadenceParts | null {
  if (!cadence || !cadence.frequency) return null;
  const time = cadence.time || '09:00';
  if (cadence.frequency === 'daily') {
    return { kind: 'daily', time, day: '', dayOfMonth: 0 };
  }
  if (cadence.frequency === 'monthly') {
    return { kind: 'monthly', time, day: '', dayOfMonth: cadence.day_of_month || 1 };
  }
  const rawDay = cadence.day || 'monday';
  const day = rawDay.charAt(0).toUpperCase() + rawDay.slice(1);
  return { kind: 'weekly', time, day, dayOfMonth: 0 };
}
